/**
 * Bodily · Size Finder Quiz
 * Asset file: assets/bodily-size-finder-quiz.js
 *
 * Include alongside size-guide-calculator.js wherever the Effortless Bra
 * size guide is rendered. Handles panel toggling and the multi-step quiz.
 *
 * Tracking: fires POST /cart/update.js with { size_calculator_used: 'true' }
 * when the customer reaches the result step. Uses update.js (not cart.js)
 * so it merges into existing cart attributes without overwriting them.
 */
(function () {
  'use strict';

  /* ── Styles ─────────────────────────────────────────────────────────
     Injected once into <head> the first time the quiz panel opens.
     All rules are scoped to #bf-app so nothing leaks to the page.
  ─────────────────────────────────────────────────────────────────── */
  function injectStyles() {
    if (document.getElementById('bf-quiz-styles')) return;
    var s = document.createElement('style');
    s.id = 'bf-quiz-styles';
    s.textContent = [
      ':root{--bf-green:#094D2C;--bf-green-deep:#062D1A;--bf-cream:#F6F1E6;',
      '--bf-cream-warm:#EFE6D2;--bf-mint:#DCEDE2;--bf-text:#1F2A26;',
      '--bf-text-muted:#5A6661;--bf-border:#D6CFBF;--bf-accent:#B98A3A;',
      '--bf-radius:10px;--bf-radius-lg:14px;}',
      '#bf-app,#bf-app *{box-sizing:border-box;}',
      '#bf-app{font-family:-apple-system,BlinkMacSystemFont,"Inter","Helvetica Neue",Helvetica,Arial,sans-serif;',
      'color:var(--bf-text);background:var(--bf-cream);line-height:1.55;font-size:16px;',
      'min-height:auto;padding:32px 16px 64px;}',
      '#bf-app .bf-shell{max-width:640px;margin:0 auto;background:#fff;',
      'border:1px solid var(--bf-border);border-radius:var(--bf-radius-lg);',
      'overflow:hidden;box-shadow:0 2px 12px rgba(9,77,44,.06);}',
      '#bf-app .bf-header{background:var(--bf-green);color:#fff;padding:20px 28px;',
      'display:flex;align-items:center;justify-content:space-between;gap:12px;}',
      '#bf-app .bf-brand{font-weight:600;font-size:14px;letter-spacing:.04em;text-transform:uppercase;}',
      '#bf-app .bf-progress{height:3px;background:var(--bf-cream-warm);overflow:hidden;}',
      '#bf-app .bf-progress-bar{height:100%;background:var(--bf-green);width:0%;transition:width 280ms ease;}',
      '#bf-app .bf-body{padding:36px 28px 28px;}',
      '#bf-app .bf-step-meta{font-size:12px;letter-spacing:.06em;text-transform:uppercase;',
      'color:var(--bf-text-muted);margin-bottom:12px;}',
      '#bf-app h1.bf-title{font-size:26px;line-height:1.25;margin:0 0 12px;font-weight:600;',
      'color:var(--bf-green-deep);letter-spacing:-.01em;}',
      '#bf-app p.bf-sub{margin:0 0 24px;color:var(--bf-text);font-size:15px;}',
      '#bf-app p.bf-help{color:var(--bf-text-muted);font-size:13px;margin-top:8px;}',
      '#bf-app .bf-options{display:flex;flex-direction:column;gap:10px;margin-bottom:24px;}',
      '#bf-app .bf-option{background:#fff;border:1.5px solid var(--bf-border);',
      'border-radius:var(--bf-radius);padding:16px 18px;text-align:left;cursor:pointer;',
      'font-family:inherit;font-size:15px;color:var(--bf-text);',
      'transition:border-color 160ms ease,background 160ms ease;',
      'display:flex;flex-direction:column;gap:4px;width:100%;}',
      '#bf-app .bf-option:hover{border-color:var(--bf-green);background:var(--bf-mint);}',
      '#bf-app .bf-option-title{font-weight:600;color:var(--bf-green-deep);}',
      '#bf-app .bf-option-desc{color:var(--bf-text-muted);font-size:14px;}',
      '#bf-app .bf-input-row{display:flex;gap:12px;margin-bottom:8px;}',
      '#bf-app .bf-input{flex:1;padding:14px 16px;border:1.5px solid var(--bf-border);',
      'border-radius:var(--bf-radius);font-family:inherit;font-size:16px;',
      'color:var(--bf-text);background:#fff;}',
      '#bf-app .bf-input:focus{outline:none;border-color:var(--bf-green);}',
      '#bf-app label.bf-label{display:block;font-size:13px;color:var(--bf-text-muted);',
      'margin-bottom:6px;letter-spacing:.02em;}',
      '#bf-app .bf-field{flex:1;}',
      '#bf-app .bf-actions{display:flex;justify-content:space-between;align-items:center;',
      'margin-top:24px;gap:12px;}',
      '#bf-app .bf-back{background:transparent;border:none;color:var(--bf-text-muted);',
      'cursor:pointer;font-family:inherit;font-size:14px;padding:8px 4px;}',
      '#bf-app .bf-back:hover{color:var(--bf-green);}',
      '#bf-app .bf-next{background:var(--bf-green);color:#fff;border:none;',
      'border-radius:var(--bf-radius);padding:12px 22px;font-family:inherit;',
      'font-size:15px;font-weight:600;cursor:pointer;transition:background 160ms ease;}',
      '#bf-app .bf-next:hover{background:var(--bf-green-deep);}',
      '#bf-app .bf-next:disabled{opacity:.45;cursor:not-allowed;}',
      '#bf-app .bf-select{width:100%;padding:14px 16px;',
      'border:1.5px solid var(--bf-border);border-radius:var(--bf-radius);',
      'font-family:inherit;font-size:16px;color:var(--bf-text);background:#fff;',
      'appearance:none;',
      'background-image:url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'8\' viewBox=\'0 0 12 8\'><path fill=\'%23094D2C\' d=\'M6 8L0 0h12z\'/></svg>");',
      'background-repeat:no-repeat;background-position:right 16px center;padding-right:40px;}',
      '#bf-app .bf-result-headline{background:var(--bf-mint);border:1px solid var(--bf-green);',
      'border-radius:var(--bf-radius-lg);padding:24px;margin-bottom:20px;}',
      '#bf-app .bf-result-eyebrow{font-size:12px;letter-spacing:.08em;text-transform:uppercase;',
      'color:var(--bf-green);font-weight:600;margin-bottom:6px;}',
      '#bf-app .bf-result-size{font-size:36px;font-weight:600;color:var(--bf-green-deep);',
      'line-height:1.1;margin-bottom:8px;}',
      '#bf-app .bf-result-context{color:var(--bf-text);font-size:14px;}',
      '#bf-app .bf-section-title{font-size:13px;letter-spacing:.06em;text-transform:uppercase;',
      'color:var(--bf-text-muted);font-weight:600;margin:24px 0 10px;}',
      '#bf-app .bf-bra-card{border:1px solid var(--bf-border);border-radius:var(--bf-radius);',
      'padding:16px 18px;margin-bottom:10px;background:#fff;}',
      '#bf-app .bf-bra-name{font-weight:600;color:var(--bf-green-deep);font-size:16px;margin-bottom:4px;}',
      '#bf-app .bf-bra-detail{font-size:14px;color:var(--bf-text-muted);margin-bottom:10px;}',
      '#bf-app .bf-bra-link{display:inline-block;color:var(--bf-green);font-weight:600;',
      'font-size:14px;text-decoration:none;border-bottom:1px solid var(--bf-green);padding-bottom:1px;}',
      '#bf-app .bf-bra-link:hover{color:var(--bf-green-deep);}',
      '#bf-app .bf-tag-cap{display:inline-block;background:var(--bf-cream-warm);',
      'color:var(--bf-accent);font-size:12px;padding:3px 8px;border-radius:999px;',
      'margin-left:6px;font-weight:600;}',
      '#bf-app details.bf-disclosure{border-top:1px solid var(--bf-border);padding-top:16px;margin-top:20px;}',
      '#bf-app details.bf-disclosure summary{cursor:pointer;font-weight:600;color:var(--bf-green);',
      'font-size:14px;list-style:none;}',
      '#bf-app details.bf-disclosure summary::-webkit-details-marker{display:none;}',
      '#bf-app details.bf-disclosure summary::after{content:" +";color:var(--bf-text-muted);}',
      '#bf-app details.bf-disclosure[open] summary::after{content:" −";}',
      '#bf-app .bf-disclosure-body{padding-top:12px;font-size:14px;color:var(--bf-text);}',
      '#bf-app .bf-restart{margin-top:24px;background:transparent;',
      'border:1.5px solid var(--bf-border);color:var(--bf-text);padding:10px 18px;',
      'border-radius:var(--bf-radius);font-family:inherit;font-size:14px;cursor:pointer;}',
      '#bf-app .bf-restart:hover{border-color:var(--bf-green);color:var(--bf-green);}',
      '#bf-app .bf-footer-note{font-size:12px;color:var(--bf-text-muted);margin-top:24px;line-height:1.5;}',
      '@media(max-width:480px){',
      '#bf-app{padding:16px 8px 40px;}',
      '#bf-app .bf-body{padding:28px 20px 20px;}',
      '#bf-app h1.bf-title{font-size:22px;}',
      '#bf-app .bf-result-size{font-size:30px;}',
      '#bf-app .bf-input-row{flex-direction:column;}}'
    ].join('');
    document.head.appendChild(s);
  }

  /* ── Quiz data ───────────────────────────────────────────────────── */
  var BRAS = {
    everything: { name: 'The Everything Bra', summary: 'A barely-there clip-down bra built for 24/7 wear, including sleep.', url: 'https://itsbodily.com/products/everything-bra' },
    effortless: { name: 'The Effortless Bra', summary: 'A barely-there pull-down bra with minimal compression, built for fluctuating sizes.', url: 'https://itsbodily.com/products/the-effortless-bra' },
    soEasy: { name: 'The So Easy Bra', summary: 'A v-neck pull-down bra with a hint more shape, for when your body is settling.', url: 'https://itsbodily.com/products/the-so-easy-bra' },
    doAnything: { name: 'The Do Anything Bra', summary: 'A 4-in-1 nursing and pumping bra with the structure of a regular bra.', url: 'https://itsbodily.com/products/the-do-anything-bra' }
  };

  var STAGES = {
    pregnant1: { label: 'Pregnant — first trimester', shortLabel: 'the first trimester', physiology: 'Your body is just starting to shift. The bigger size changes come later — right now, comfort is the only rule.', permissive: true, recommendations: [{ bra: 'everything', useNote: 'All-day wear, including sleep' }, { bra: 'effortless', useNote: 'All-day wear' }, { bra: 'soEasy', useNote: 'All-day wear, with a hint more shape' }, { bra: 'doAnything', useNote: 'For pump sessions or structured wear' }], avoidTypes: [], sizingNudge: 'between' },
    pregnant2: { label: 'Pregnant — second trimester', shortLabel: 'the second trimester', physiology: 'Your rib cage is widening and your breasts are growing. A bra with a hint more shape often feels right around now — but the rule is still comfort.', permissive: true, recommendations: [{ bra: 'soEasy', useNote: 'All-day wear, with a hint more shape' }, { bra: 'everything', useNote: 'All-day wear, including sleep' }, { bra: 'effortless', useNote: 'All-day wear' }, { bra: 'doAnything', useNote: 'For pump sessions or structured wear' }], avoidTypes: [], sizingNudge: 'between' },
    pregnant3: { label: 'Pregnant — third trimester', shortLabel: 'the third trimester', physiology: 'Your rib cage is at its widest and your breasts are at their fullest. Most people reach for soft, stretchy, no-compression — often the same bras they will live in for the first months after birth.', permissive: true, recommendations: [{ bra: 'everything', useNote: 'All-day wear, including sleep' }, { bra: 'effortless', useNote: 'All-day wear' }, { bra: 'soEasy', useNote: 'All-day wear, with a hint more shape' }, { bra: 'doAnything', useNote: 'For pump sessions or structured wear' }], avoidTypes: [], sizingNudge: 'between' },
    stage1: { label: '0–3 months after birth', shortLabel: 'the first three months', physiology: 'Your milk supply is establishing and your breasts fluctuate the most right now. The soft, stretchy bras are built for this window. The structured bras are still here when you need them — for pump sessions, errands, or whatever comes up — just with a wear-time cap.', recommendations: [{ bra: 'everything', useNote: 'All-day wear, including sleep' }, { bra: 'effortless', useNote: 'All-day wear' }, { bra: 'soEasy', useNote: 'For shorter stretches, up to 3 hours', hourCap: 3 }, { bra: 'doAnything', useNote: 'For pump sessions, up to 1 hour at a time', hourCap: 1 }], avoidTypes: ['underwire bras', 'cupped bras', 'tight, lifting, or shaping bras'], sizingNudge: 'down' },
    stage2: { label: '3–6 months after birth', shortLabel: 'months three through six', physiology: 'Fluctuations are smaller now. Your body has settled into a more predictable rhythm, so you can layer in more structure during the day. Soft bras still belong in your rotation, especially at night.', recommendations: [{ bra: 'soEasy', useNote: 'All-day wear, with a hint more shape' }, { bra: 'everything', useNote: 'All-day wear, including sleep' }, { bra: 'effortless', useNote: 'All-day wear' }, { bra: 'doAnything', useNote: 'For pump sessions, up to 3 hours at a time', hourCap: 3 }], avoidTypes: [], sizingNudge: 'down' },
    stage3: { label: '6+ months after birth', shortLabel: 'six months and beyond', physiology: 'Your breasts have largely stabilized and the risk of plugged ducts has dropped. Most bra types work for everyday wear now; structured bras are fine for the full day.', recommendations: [{ bra: 'doAnything', useNote: 'For pump sessions or structured wear' }, { bra: 'soEasy', useNote: 'All-day wear, with a hint more shape' }, { bra: 'everything', useNote: 'All-day wear, including sleep' }, { bra: 'effortless', useNote: 'All-day wear' }], avoidTypes: [], sizingNudge: 'true' },
    none: { label: 'Not pregnant or breastfeeding', shortLabel: 'right now', physiology: 'You are not in a fluctuation window, so any of our bras can work for everyday wear. Pick the one that matches the support and structure you want.', recommendations: [{ bra: 'doAnything', useNote: 'For pump sessions or structured wear' }, { bra: 'soEasy', useNote: 'All-day wear, with a hint more shape' }, { bra: 'everything', useNote: 'All-day wear, including sleep' }, { bra: 'effortless', useNote: 'All-day wear' }], avoidTypes: [], sizingNudge: 'true' }
  };

  var SIZE_CHARTS = {
    everything: { S: ['32A','32B','32C','32D','32DD','34A','34B','34C','36A'], M: ['32DDD','34D','34DD','34DDD','36B','36C','36D','36DD','38A','38B','38C','40A'], L: ['34G','36DDD','36G','38D','38DD','38DDD','40B','40C','40D','42A','42B','42C','44A'], XL: ['38G','40DD','40DDD','40G','42D','42DD','42DDD','42G','44B','44C','44D','44DD','44DDD','46B','46C'] },
    effortless: { S: ['32A','32B','32C','32D','32DD','34A','34B','34C','36A'], M: ['32DDD','34D','34DD','34DDD','36B','36C','36D','36DD','38A','38B','38C','40A'], L: ['34G','36DDD','36G','38D','38DD','38DDD','40B','40C','40D','42A','42B','42C','44A'], XL: ['38G','40DD','40DDD','40G','42D','42DD','42DDD','42G','44B','44C','44D','44DD','44DDD','46B','46C'] },
    soEasy: { S: ['32A','32B','32C','32D','34A','34B','34C'], M: ['32DD','34D','34DD','36A','36B','36C','38A','38B','38C'], L: ['36D','36DD','38D','40A','40B','40C'], XL: ['36DDD','38DD','40D','42A','42B','42C'] },
    doAnything: { S: ['32B','32C','32D','34B','34C'], M: ['32DD','32DDD','34D','34DD','36B','36C','38B'], L: ['34DDD','36D','36DD','38C','40B'], XL: ['36DDD','38D','38DD','40C','40D','40DD','42B','42C'] }
  };

  var CUPS = ['A','B','C','D','DD','DDD','G','H','I','J','K'];
  var BANDS = [30, 32, 34, 36, 38, 40, 42, 44, 46];

  /* ── State ───────────────────────────────────────────────────────── */
  function initialState() {
    return { step: 'start', history: [], path: null, stage: null, underbust: null, bust: null, currentBand: null, currentCup: null, braAge: null, hook: null, bandFeel: null, cupFeel: null };
  }
  var state = initialState();
  var $body = null;
  var $progress = null;

  function ensureMounted() {
    if (!$body) $body = document.getElementById('bf-body');
    if (!$progress) $progress = document.getElementById('bf-progress-bar');
    return !!($body && $progress);
  }

  /* ── DOM builder ─────────────────────────────────────────────────── */
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function(k) {
        if (k === 'class') node.className = attrs[k];
        else if (k === 'html') node.innerHTML = attrs[k];
        else if (k.indexOf('on') === 0) node.addEventListener(k.slice(2), attrs[k]);
        else node.setAttribute(k, attrs[k]);
      });
    }
    if (children) {
      var arr = Array.isArray(children) ? children : [children];
      arr.forEach(function(c) {
        if (c == null) return;
        if (typeof c === 'string') node.appendChild(document.createTextNode(c));
        else node.appendChild(c);
      });
    }
    return node;
  }

  /* ── Navigation ──────────────────────────────────────────────────── */
  function go(step) { state.history.push(state.step); state.step = step; render(); }
  function back() { var prev = state.history.pop(); if (prev) { state.step = prev; render(); } }

  function pathSteps() {
    if (state.path === 'tape') return ['start','stage','tapeUnderbust','tapeBust','result'];
    if (state.path === 'guided') return ['start','stage','guidedCurrent','guidedAge','guidedHook','guidedBand','guidedCup','result'];
    return ['start'];
  }

  function progressPct() {
    var steps = pathSteps();
    var i = steps.indexOf(state.step);
    if (i < 0) return 0;
    return Math.round(((i + 1) / steps.length) * 100);
  }

  /* ── Size math ───────────────────────────────────────────────────── */
  function calcBandFromUnderbust(u) {
    var rounded = Math.round(u);
    var band = rounded % 2 === 0 ? rounded : (rounded + 1);
    if (band >= 36) {
      var lower = (rounded % 2 === 0) ? rounded : (rounded - 1);
      if (Math.abs(lower - u) < Math.abs(band - u)) band = lower;
    }
    if (band < BANDS[0]) band = BANDS[0];
    if (band > BANDS[BANDS.length - 1]) band = BANDS[BANDS.length - 1];
    return band;
  }

  function calcCupFromDiff(diff) {
    var rounded = Math.round(diff);
    var idx = Math.max(0, Math.min(CUPS.length - 1, rounded - 1));
    return CUPS[idx];
  }

  function mapToBodilySize(braKey, band, cup) {
    var chart = SIZE_CHARTS[braKey];
    if (!chart) return null;
    var key = String(band) + cup;
    var sizes = ['S','M','L','XL'];
    for (var i = 0; i < sizes.length; i++) {
      if (chart[sizes[i]].indexOf(key) !== -1) return sizes[i];
    }
    return null;
  }


  function applyStageNudge(band, cup) { return { band: band, cup: cup, nudged: null }; }

  function computeGuidedSize() {
    var bandAdj = 0;
    var cupAdj = 0;
    var isOld = (state.braAge === 'old' || state.braAge === 'mid');
    if (!isOld) {
      if (state.bandFeel === 'loose') bandAdj = -1;
      else if (state.bandFeel === 'tight') bandAdj = 1;
      if (state.hook === 'tightest') bandAdj -= 1;
    } else {
      if (state.hook === 'loosest') bandAdj = 1;
      else if (state.hook === 'tightest' && state.bandFeel === 'tight') bandAdj = -1;
      else bandAdj = 0;
    }
    switch (state.cupFeel) {
      case 'smooth': cupAdj = 0; break;
      case 'slightSpill': cupAdj = 1; break;
      case 'bigSpill': cupAdj = 2; break;
      case 'gap': cupAdj = -1; break;
      case 'gore': cupAdj = 1; break;
    }
    cupAdj -= bandAdj;
    bandAdj = Math.max(-2, Math.min(2, bandAdj));
    cupAdj = Math.max(-3, Math.min(3, cupAdj));
    var curBandIdx = BANDS.indexOf(parseInt(state.currentBand));
    var newBandIdx = Math.max(0, Math.min(BANDS.length - 1, curBandIdx + bandAdj));
    var curCupIdx = CUPS.indexOf(state.currentCup);
    var newCupIdx = Math.max(0, Math.min(CUPS.length - 1, curCupIdx + cupAdj));
    return { band: BANDS[newBandIdx], cup: CUPS[newCupIdx], bandAdj: bandAdj, cupAdj: cupAdj };
  }

  /* ── Render ──────────────────────────────────────────────────────── */
  function scrollQuizToTop() {
    var candidates = [
      document.querySelector('.product-popup-modal__content-info'),
      document.querySelector('.product-popup-modal__content'),
      document.getElementById('sg-quiz-panel')
    ];
    for (var i = 0; i < candidates.length; i++) {
      if (candidates[i]) { candidates[i].scrollTop = 0; return; }
    }
  }

  function render() {
    if (!ensureMounted()) return;
    $progress.style.width = progressPct() + '%';
    $body.innerHTML = '';
    var fn = STEPS[state.step];
    if (fn) fn();
    scrollQuizToTop();
  }

  /* ── Step renderers ──────────────────────────────────────────────── */
  function stepMeta(text) { return el('div', { 'class': 'bf-step-meta' }, text); }

  function actions() {
    var row = el('div', { 'class': 'bf-actions' });
    if (state.history.length > 0) row.appendChild(el('button', { 'class': 'bf-back', 'onclick': back }, '← Back'));
    else row.appendChild(el('span'));
    return row;
  }

  function optionList(items) {
    var wrap = el('div', { 'class': 'bf-options' });
    items.forEach(function(item) {
      var btn = el('button', { 'class': 'bf-option', 'onclick': item.onclick }, [
        el('span', { 'class': 'bf-option-title' }, item.title),
        item.desc ? el('span', { 'class': 'bf-option-desc' }, item.desc) : null
      ]);
      wrap.appendChild(btn);
    });
    return wrap;
  }

  function renderStart() {
    $body.appendChild(stepMeta('Step 1'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'Find your size'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Two ways to do this. Pick whichever fits your situation.'));
    $body.appendChild(optionList([
      { title: 'I have a tape measure', desc: 'Quick math from your underbust and bust measurements.', onclick: function() { state.path = 'tape'; go('stage'); } },
      { title: 'Walk me through it', desc: 'No tape needed. We will work from the bra you are wearing right now.', onclick: function() { state.path = 'guided'; go('stage'); } }
    ]));
    $body.appendChild(el('p', { 'class': 'bf-footer-note' }, 'Sizing is not fixed. Your body changes through pregnancy, recovery, and breastfeeding — so we will ask where you are before recommending a size.'));
  }

  function renderStage() {
    $body.appendChild(stepMeta('Step 2 · Where you are'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'Where are you right now?'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Your body is in different places at different stages. The right bra changes with it.'));
    $body.appendChild(optionList([
      { title: 'Pregnant — first trimester', onclick: function() { state.stage = 'pregnant1'; advanceFromStage(); } },
      { title: 'Pregnant — second trimester', onclick: function() { state.stage = 'pregnant2'; advanceFromStage(); } },
      { title: 'Pregnant — third trimester', onclick: function() { state.stage = 'pregnant3'; advanceFromStage(); } },
      { title: '0–3 months after birth', onclick: function() { state.stage = 'stage1'; advanceFromStage(); } },
      { title: '3–6 months after birth', onclick: function() { state.stage = 'stage2'; advanceFromStage(); } },
      { title: '6+ months after birth', onclick: function() { state.stage = 'stage3'; advanceFromStage(); } },
      { title: 'Not pregnant or breastfeeding', onclick: function() { state.stage = 'none'; advanceFromStage(); } }
    ]));
    $body.appendChild(actions());
  }

  function advanceFromStage() {
    if (state.path === 'tape') go('tapeUnderbust');
    else go('guidedCurrent');
  }

  function renderTapeUnderbust() {
    $body.appendChild(stepMeta('Step 3 · Underbust'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'Measure your underbust'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Wrap a soft tape around your rib cage, directly under your breasts. Keep it level and snug, but not tight enough to dig in. This becomes your band size.'));
    var input = el('input', { 'class': 'bf-input', 'type': 'number', 'step': '0.25', 'min': '24', 'max': '50', 'placeholder': 'Inches (e.g., 32)', 'value': state.underbust || '' });
    var next = el('button', { 'class': 'bf-next', 'onclick': function() { go('tapeBust'); } }, 'Continue');
    if (!state.underbust) next.disabled = true;
    input.addEventListener('input', function(e) { state.underbust = e.target.value; next.disabled = !state.underbust || isNaN(parseFloat(state.underbust)); });
    $body.appendChild(input);
    $body.appendChild(el('p', { 'class': 'bf-help' }, 'Take a few measurements and use the average if you can. Your band size is the number rounded to the nearest even inch.'));
    var row = el('div', { 'class': 'bf-actions' });
    row.appendChild(el('button', { 'class': 'bf-back', 'onclick': back }, '← Back'));
    row.appendChild(next);
    $body.appendChild(row);
  }

  function renderTapeBust() {
    $body.appendChild(stepMeta('Step 4 · Bust'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'Measure your bust'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Wrap the tape around the fullest part of your breasts — usually at nipple height. Keep it level and snug. Do not press in. The difference between this and your underbust gives us the cup.'));
    var input = el('input', { 'class': 'bf-input', 'type': 'number', 'step': '0.25', 'min': '26', 'max': '60', 'placeholder': 'Inches (e.g., 38)', 'value': state.bust || '' });
    var next = el('button', { 'class': 'bf-next', 'onclick': function() { go('result'); } }, 'See my size');
    if (!state.bust) next.disabled = true;
    input.addEventListener('input', function(e) { state.bust = e.target.value; next.disabled = !state.bust || isNaN(parseFloat(state.bust)); });
    $body.appendChild(input);
    $body.appendChild(el('p', { 'class': 'bf-help' }, 'If you can, take this measurement standing, leaning forward, and lying down — then average them.'));
    var row = el('div', { 'class': 'bf-actions' });
    row.appendChild(el('button', { 'class': 'bf-back', 'onclick': back }, '← Back'));
    row.appendChild(next);
    $body.appendChild(row);
  }

  function renderGuidedCurrent() {
    $body.appendChild(stepMeta('Step 3 · The bra you are wearing'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'What size is the bra you have on?'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Use the band and cup printed on the tag. If you are between bras or unsure, pick the closest match.'));
    var row = el('div', { 'class': 'bf-input-row' });
    var bandSel = el('select', { 'class': 'bf-select' });
    bandSel.appendChild(el('option', { 'value': '' }, 'Select'));
    BANDS.forEach(function(b) {
      var opt = el('option', { 'value': String(b) }, String(b));
      if (state.currentBand == b) opt.selected = true;
      bandSel.appendChild(opt);
    });
    var cupSel = el('select', { 'class': 'bf-select' });
    cupSel.appendChild(el('option', { 'value': '' }, 'Select'));
    CUPS.forEach(function(c) {
      var opt = el('option', { 'value': c }, c);
      if (state.currentCup === c) opt.selected = true;
      cupSel.appendChild(opt);
    });
    var next = el('button', { 'class': 'bf-next', 'onclick': function() { go('guidedAge'); } }, 'Continue');
    function validateNext() { next.disabled = !state.currentBand || !state.currentCup; }
    bandSel.addEventListener('change', function(e) { state.currentBand = e.target.value; validateNext(); });
    cupSel.addEventListener('change', function(e) { state.currentCup = e.target.value; validateNext(); });
    validateNext();
    row.appendChild(el('div', { 'class': 'bf-field' }, [el('label', { 'class': 'bf-label' }, 'Band'), bandSel]));
    row.appendChild(el('div', { 'class': 'bf-field' }, [el('label', { 'class': 'bf-label' }, 'Cup'), cupSel]));
    $body.appendChild(row);
    var actionRow = el('div', { 'class': 'bf-actions' });
    actionRow.appendChild(el('button', { 'class': 'bf-back', 'onclick': back }, '← Back'));
    actionRow.appendChild(next);
    $body.appendChild(actionRow);
  }

  function renderGuidedAge() {
    $body.appendChild(stepMeta('Step 4 · The bra age'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'How old is this bra?'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Elastic stretches over time. The age of the bra changes how we read the fit.'));
    $body.appendChild(optionList([
      { title: 'Less than 6 months', onclick: function() { state.braAge = 'new'; go('guidedHook'); } },
      { title: '6 to 12 months', onclick: function() { state.braAge = 'mid'; go('guidedHook'); } },
      { title: 'More than a year', onclick: function() { state.braAge = 'old'; go('guidedHook'); } },
      { title: 'I am not sure', onclick: function() { state.braAge = 'unsure'; go('guidedHook'); } }
    ]));
    $body.appendChild(actions());
  }

  function renderGuidedHook() {
    $body.appendChild(stepMeta('Step 5 · Hook position'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'Which hook is fastened?'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Look at the back closure. A new bra should sit on the loosest hook so there is room to tighten as the elastic gives.'));
    $body.appendChild(optionList([
      { title: 'Loosest', desc: 'Closest to the band edge.', onclick: function() { state.hook = 'loosest'; go('guidedBand'); } },
      { title: 'Middle', onclick: function() { state.hook = 'middle'; go('guidedBand'); } },
      { title: 'Tightest', desc: 'Pulls the band closest to your body.', onclick: function() { state.hook = 'tightest'; go('guidedBand'); } }
    ]));
    $body.appendChild(actions());
  }

  function renderGuidedBand() {
    $body.appendChild(stepMeta('Step 6 · The band'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'Settle in, then check the band'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Lean forward at the waist and gently lift each breast fully into its cup. Stand back up. Now slide two fingers under the band at your back.'));
    $body.appendChild(optionList([
      { title: 'Snug', desc: 'Slight resistance, fingers fit comfortably.', onclick: function() { state.bandFeel = 'snug'; go('guidedCup'); } },
      { title: 'Loose', desc: 'I could fit my fist under there.', onclick: function() { state.bandFeel = 'loose'; go('guidedCup'); } },
      { title: 'Tight', desc: 'I can barely fit two fingers.', onclick: function() { state.bandFeel = 'tight'; go('guidedCup'); } }
    ]));
    $body.appendChild(el('p', { 'class': 'bf-footer-note' }, 'The band does most of the supporting work — straps are along for the ride. So we start here.'));
    $body.appendChild(actions());
  }

  function renderGuidedCup() {
    $body.appendChild(stepMeta('Step 7 · The cups'));
    $body.appendChild(el('h1', { 'class': 'bf-title' }, 'Now look at the cups'));
    $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Stand normally and look in the mirror — with or without a thin shirt on top. What do you see?'));
    $body.appendChild(optionList([
      { title: 'Smooth', desc: 'Fabric is flush against the breast.', onclick: function() { state.cupFeel = 'smooth'; go('result'); } },
      { title: 'Slight spillage', desc: 'Small bulge over the top or side of the cup.', onclick: function() { state.cupFeel = 'slightSpill'; go('result'); } },
      { title: 'Significant spillage', desc: 'Cup is clearly cutting into the breast tissue.', onclick: function() { state.cupFeel = 'bigSpill'; go('result'); } },
      { title: 'Gaps or wrinkles', desc: 'Fabric is pulling away from the breast.', onclick: function() { state.cupFeel = 'gap'; go('result'); } },
      { title: 'The center piece does not lie flat', desc: 'It pushes off the chest between the cups.', onclick: function() { state.cupFeel = 'gore'; go('result'); } }
    ]));
    $body.appendChild(actions());
  }

  function renderResult() {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { size_calculator_used: 'true' } })
    }).catch(function(err) { console.log('Size calculator tracking failed', err); });

    var band, cup, why;
    if (state.path === 'tape') {
      var u = parseFloat(state.underbust);
      var b = parseFloat(state.bust);
      band = calcBandFromUnderbust(u);
      var diff = Math.max(1, b - u);
      cup = calcCupFromDiff(diff);
      why = 'From your measurements: underbust ' + u + '", bust ' + b + '", a ' + Math.round(diff) + '-inch difference. That maps to a ' + band + cup + ' starting point.';
    } else {
      var guided = computeGuidedSize();
      band = guided.band;
      cup = guided.cup;
      var parts = [];
      if (guided.bandAdj === 0) parts.push('your band reads as correct');
      else if (guided.bandAdj < 0) parts.push('your band is reading ' + Math.abs(guided.bandAdj) + ' size' + (Math.abs(guided.bandAdj) > 1 ? 's' : '') + ' too big');
      else parts.push('your band is reading ' + guided.bandAdj + ' size' + (guided.bandAdj > 1 ? 's' : '') + ' too small');
      if (guided.cupAdj === 0) parts.push('cups are sitting right');
      else if (guided.cupAdj < 0) parts.push('cups should drop ' + Math.abs(guided.cupAdj));
      else parts.push('cups should come up ' + guided.cupAdj);
      why = 'Starting from ' + state.currentBand + state.currentCup + ': ' + parts.join(', ') + '. That brings you to ' + band + cup + '.';
    }

    var stage = STAGES[state.stage];
    var adjusted = applyStageNudge(band, cup);

    var recList = stage.recommendations.map(function(rec) {
      var copy = {};
      for (var k in rec) copy[k] = rec[k];
      copy.size = mapToBodilySize(rec.bra, adjusted.band, adjusted.cup);
      return copy;
    }).filter(function(rec) { return rec.size !== null; });

    var sizeOrder = ['S', 'M', 'L', 'XL'];
    var uniqueSizes = recList.reduce(function(acc, r) {
      if (r.size && acc.indexOf(r.size) === -1) acc.push(r.size);
      return acc;
    }, []);
    uniqueSizes.sort(function(a, b) { return sizeOrder.indexOf(a) - sizeOrder.indexOf(b); });
    var betweenSizes = uniqueSizes.length > 1;
    var primarySize = uniqueSizes.length > 0 ? uniqueSizes.join(' / ') : '—';

    var headline = el('div', { 'class': 'bf-result-headline' }, [
      el('div', { 'class': 'bf-result-eyebrow' }, betweenSizes ? 'Your Bodily size range' : 'Your Bodily size'),
      el('div', { 'class': 'bf-result-size' }, primarySize),
      el('div', { 'class': 'bf-result-context' }, recList.length > 0
        ? 'That is ' + adjusted.band + adjusted.cup + ' in standard sizing — for ' + stage.shortLabel + '.' + (betweenSizes ? ' Size varies by style — each bra below shows your fit for that specific cut.' : '')
        : adjusted.band + adjusted.cup + ' sits outside the range we currently carry. Reach out and we will help you find the closest fit.')
    ]);
    $body.appendChild(headline);

    $body.appendChild(el('p', { 'class': 'bf-sub' }, stage.physiology));

    if (stage.permissive) {
      $body.appendChild(el('p', { 'class': 'bf-sub' }, 'Any of our bras can work during pregnancy. Comfort is the rule.'));
    }

    if (recList.length > 0) {
      var sectionTitle = stage.permissive ? 'What most people reach for first' : 'Your recommendations';
      $body.appendChild(el('div', { 'class': 'bf-section-title' }, sectionTitle));
      recList.forEach(function(rec) {
        var bra = BRAS[rec.bra];
        var card = el('div', { 'class': 'bf-bra-card' }, [
          el('div', { 'class': 'bf-bra-name' }, bra.name + ' · ' + rec.size),
          el('div', { 'class': 'bf-bra-detail', 'style': 'color:var(--bf-green);font-weight:600;margin-bottom:4px;' }, rec.useNote),
          el('div', { 'class': 'bf-bra-detail' }, bra.summary),
          el('a', { 'class': 'bf-bra-link', 'href': bra.url, 'target': '_blank' }, 'View ' + bra.name + ' →')
        ]);
        $body.appendChild(card);
      });
    }

    var cappedRecs = recList.filter(function(r) { return r.hourCap; });
    if (cappedRecs.length > 0 || (stage.avoidTypes && stage.avoidTypes.length > 0)) {
      var guidanceBody = [];
      guidanceBody.push(el('p', {}, 'In the first months of breastfeeding, milk supply is still establishing and breast tissue is most vulnerable to engorgement, plugged ducts, and mastitis. Compression — from shaping, lifting, underwire, padding, or any extra structure — can restrict milk flow and contribute to these issues.'));
      guidanceBody.push(el('p', {}, 'This guidance applies to any structured bra during this window, not just ours. The wear-time limits below cap structured-bra wear in general — whether the bra is Bodily or another brand. Soft, stretchy, no-compression bras do not carry the same caps.'));

      if (cappedRecs.length > 0) {
        guidanceBody.push(el('div', { 'class': 'bf-section-title' }, 'Wear-time limits'));
        cappedRecs.forEach(function(rec) {
          var bra = BRAS[rec.bra];
          var cap = rec.hourCap + ' hour' + (rec.hourCap > 1 ? 's' : '') + ' max at a time';
          guidanceBody.push(el('div', { 'class': 'bf-bra-card' }, [
            el('div', { 'class': 'bf-bra-name' }, [bra.name + ' · ' + rec.size, el('span', { 'class': 'bf-tag-cap' }, cap)]),
            el('div', { 'class': 'bf-bra-detail' }, rec.useNote)
          ]));
        });
      }

      if (stage.avoidTypes && stage.avoidTypes.length > 0) {
        guidanceBody.push(el('div', { 'class': 'bf-section-title' }, 'Set these aside for now'));
        guidanceBody.push(el('p', { 'class': 'bf-bra-detail' }, 'During this window, leave out: ' + stage.avoidTypes.join(', ') + '. The same applies to any brand.'));
      }

      $body.appendChild(el('details', { 'class': 'bf-disclosure' }, [
        el('summary', {}, 'Prone to plugged ducts? See wear-time guidance'),
        el('div', { 'class': 'bf-disclosure-body' }, guidanceBody)
      ]));
    }

    var sizingNote = stage.sizingNudge === 'between'
      ? 'Bodily rule: if you are in between two sizes during pregnancy, size up. Your rib cage is still expanding, and a snug band that fits today will not fit in two months.'
      : stage.sizingNudge === 'down'
        ? 'Bodily rule: if you are in between two sizes while breastfeeding or in the first six months postpartum, size down. A snugger band stays in place; a looser one can shift and encourage milk pooling.'
        : null;

    $body.appendChild(el('details', { 'class': 'bf-disclosure' }, [
      el('summary', {}, 'Why this size'),
      el('div', { 'class': 'bf-disclosure-body' }, [
        el('p', {}, why),
        sizingNote ? el('p', {}, sizingNote) : null
      ])
    ]));

    $body.appendChild(el('p', { 'class': 'bf-footer-note' }, 'A note on asymmetry: most breasts are slightly different sizes — and a meaningful number are noticeably different. If that is you, fit to the larger side and tighten the strap on the smaller side.'));

    $body.appendChild(el('button', { 'class': 'bf-restart', 'onclick': function() { state = initialState(); render(); } }, '↺ Start over'));
  }

  var STEPS = {
    start: renderStart,
    stage: renderStage,
    tapeUnderbust: renderTapeUnderbust,
    tapeBust: renderTapeBust,
    guidedCurrent: renderGuidedCurrent,
    guidedAge: renderGuidedAge,
    guidedHook: renderGuidedHook,
    guidedBand: renderGuidedBand,
    guidedCup: renderGuidedCup,
    result: renderResult
  };

  /* ── Panel toggle ────────────────────────────────────────────────── */
  function openQuiz() {
    injectStyles();
    state = initialState();
    $body = null;
    $progress = null;
    (function tryInit() {
      if (ensureMounted()) render();
      else setTimeout(tryInit, 50);
    })();
  }

  /* ── Boot — delegated listener, no timing dependencies ──────────── */
  document.addEventListener('click', function (e) {
    var t = e.target;

    if (t && t.id === 'sg-open-quiz') {
      var chart = document.getElementById('sg-chart-panel');
      var quiz = document.getElementById('sg-quiz-panel');
      if (chart) chart.style.display = 'none';
      if (quiz) quiz.style.display = 'block';
      openQuiz();
      return;
    }

    if (t && t.id === 'sg-back-to-chart') {
      var chart = document.getElementById('sg-chart-panel');
      var quiz = document.getElementById('sg-quiz-panel');
      if (quiz) quiz.style.display = 'none';
      if (chart) chart.style.display = 'block';
    }
  });
})();
